import { useMessages } from '@hooks/useMessages';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DropdownItem } from 'reactstrap';
import { useFormatters } from '@hooks/useFormatters';
import { logOnError } from '@src/@data/utility/errors';
import QrScannerBackButton from '@src/components/organisms/qr-scanner/QrScannerBackButton';
import { PromoCode } from '@src/@data/types/promoCodes';
import promoCodes from '@src/@data/api/promoCodes';
import SubscriptionItem from '@appcomponents/molecules/subscription-item/SubscriptionItem';
import CreateReservationFromPromoModal from '@appcomponents/organisms/create-reservation/CreateReservationFromPromoModal';
import {
  PromoCodeStatus,
  ResolveStateForPromoCode,
} from '@src/pages/company/promo-codes/PromoCodeState';

interface QrScannerSubscriptionItemProps {
  companyId: string;
  subscriptionId: string;
  onBack: () => void;
}

export const QrScannerSubscriptionItemItem: React.FC<QrScannerSubscriptionItemProps> =
  ({ companyId, subscriptionId, onBack }) => {
    const { messages } = useMessages();
    const { moneyFormatter } = useFormatters();
    const [isLoading, setIsLoading] = useState(false);
    const [promoCode, setPromoCode] = useState<PromoCode>();
    const [error, setError] = useState<Error>();

    const [createReservationShown, setCreateReservationShown] = useState(false);

    const canCreateReservation = useMemo(
      () =>
        !!promoCode &&
        ResolveStateForPromoCode(promoCode).type === PromoCodeStatus.Active,
      [promoCode]
    );

    useEffect(() => {
      promoCodes
        .companyPromoCodeDetails(companyId, subscriptionId)
        .then((code) => {
          setPromoCode(code);
        })
        .catch((err) => {
          logOnError(err);
          setError(err);
          setPromoCode(undefined);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, [companyId, moneyFormatter, subscriptionId]);

    return (
      <>
        {!isLoading && (!!promoCode || !!error) && (
          <>
            <QrScannerBackButton
              message={
                promoCode
                  ? messages('qr_scanner_dropdown_subscription_found')
                  : messages('qr_scanner_dropdown_subscription_not_found')
              }
              onBack={() => setCreateReservationShown(true)}
            />
            {!!promoCode && (
              <>
                <DropdownItem divider />
                <DropdownItem style={{ width: '100%' }}>
                  <SubscriptionItem
                    promoCode={promoCode}
                    companyId={companyId}
                  />
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem text>
                  <Button
                    className="w-100"
                    outline
                    color="primary"
                    size="sm"
                    onClick={() => setCreateReservationShown(true)}
                    disabled={!canCreateReservation}
                  >
                    {messages(
                      'qr_scanner_dropdown_subscription_create_reservation'
                    )}
                  </Button>
                </DropdownItem>
              </>
            )}
          </>
        )}
        {!!promoCode && canCreateReservation && createReservationShown && (
          <CreateReservationFromPromoModal
            companyId={companyId}
            promoCode={promoCode}
            isOpen
            onCancel={onBack}
          />
        )}
      </>
    );
  };

export default QrScannerSubscriptionItemItem;
